import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import {
    LOGO_BOX, LOGO_SUBPATHS_D, LogoLoop, clearLogo, drawLogoFrame, fromEpochMs, type LogoTheme,
} from "./logoFrame";
import type { LogoWorkerMessage, LogoWorkerReply } from "./logoWorker";
// Inlined into the bundle, so a page refresh never waits on fetching it
// behind the page's own assets before the logo can start drawing.
import LogoWorker from "./logoWorker?worker&inline";

/** What a `finish` reports back. */
export type LogoFinishWatch = {
    /** When (`performance.now()` ms) the logo will have come to rest fully
     *  formed: known as soon as `finish` is asked, since the motion from
     *  there is deterministic. Called again, with a later time, if the
     *  draw is replayed on another renderer (worker failure, StrictMode's
     *  dev remount). */
    restsAt: (atMs: number) => void;
    /** Once it rests there. */
    rested: () => void;
};

export type LogoHandle = {
    /** Starts the draw, which spins if `finish` hasn't come by its end. */
    start: (theme: LogoTheme) => void;
    /** Settles on the fully formed logo, reporting when it will rest
     *  there and once it does. */
    finish: (watch: LogoFinishWatch) => void;
    /** Clears the canvas and stops any draw in progress. */
    reset: () => void;
};

type LogoProps = {
    /** Scale the logo grows to; the canvas is sized for it so the
     *  scale-up is drawn in canvas space and never upscaled by CSS. */
    scale: number;
};

/** What `Logo` needs from either renderer: the worker or the fallback. */
type Driver = {
    start: (theme: LogoTheme, endScale: number) => void;
    finish: (watch: LogoFinishWatch) => void;
    reset: () => void;
    resize: (cssSize: number, dpr: number, endScale: number) => void;
    dispose: () => void;
};

const canWorkerDraw =
    typeof Worker !== "undefined"
    && typeof HTMLCanvasElement !== "undefined"
    && "transferControlToOffscreen" in HTMLCanvasElement.prototype;

const getDpr = () => Math.min(window.devicePixelRatio || 1, 3);

/** Subpath lengths in viewBox units, measured once by the browser (the
 *  worker has no SVG geometry, and they never change). */
let subpathLengths: number[] | null = null;
function measureSubpaths(): number[] {
    if (subpathLengths) return subpathLengths;
    const ns = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(ns, "svg");
    svg.setAttribute("style", "position:absolute;width:0;height:0;visibility:hidden");
    const path = document.createElementNS(ns, "path");
    svg.appendChild(path);
    document.body.appendChild(svg);
    subpathLengths = LOGO_SUBPATHS_D.map((d) => {
        path.setAttribute("d", d);
        return path.getTotalLength();
    });
    svg.remove();
    return subpathLengths;
}

/** Off-main-thread renderer: the canvas is handed to `logoWorker.ts`. */
function createWorkerDriver(
    canvas: HTMLCanvasElement,
    cssSize: number,
    dpr: number,
    onError: () => void,
): Driver {
    const worker = new LogoWorker();
    const post = (msg: LogoWorkerMessage, transfer: Transferable[] = []) => worker.postMessage(msg, transfer);
    try {
        const offscreen = canvas.transferControlToOffscreen();
        post({ type: "init", canvas: offscreen, cssSize, dpr, subpathLengths: measureSubpaths() }, [offscreen]);
    } catch (err) {
        worker.terminate();
        throw err;
    }

    let nextId = 0;
    let pending: { id: number; watch: LogoFinishWatch } | null = null;
    worker.onmessage = (e: MessageEvent<LogoWorkerReply>) => {
        const reply = e.data;
        if (reply.type === "unsupported") return onError();
        if (!pending || reply.id !== pending.id) return;
        if (reply.type === "finishing") {
            pending.watch.restsAt(fromEpochMs(reply.restsAtEpochMs));
        } else {
            pending.watch.rested();
            pending = null;
        }
    };
    worker.onerror = onError;

    return {
        start: (theme, endScale) => post({ type: "start", theme, endScale }),
        finish: (watch) => {
            pending = { id: ++nextId, watch };
            post({ type: "finish", id: pending.id });
        },
        reset: () => {
            pending = null;
            post({ type: "reset" });
        },
        resize: (nextCssSize, nextDpr, endScale) => post({ type: "resize", cssSize: nextCssSize, dpr: nextDpr, endScale }),
        dispose: () => worker.terminate(),
    };
}

/** Main-thread renderer, for browsers without OffscreenCanvas (or if the
 *  worker fails to load). Same frames, but pauses with the main thread. */
function createMainThreadDriver(canvas: HTMLCanvasElement, cssSize: number, dpr: number): Driver {
    const ctx = canvas.getContext("2d");
    let size = { cssSize, dpr };
    let generation = 0;
    let loop: LogoLoop | null = null;
    /** When `loop` was last advanced to (`performance.now()` ms). */
    let last = 0;
    let look: { theme: LogoTheme; endScale: number } | null = null;
    /** Same contract as the worker: a finish may arrive before its start. */
    let finishWatch: LogoFinishWatch | null = null;
    const redraw = () => {
        if (ctx && loop && look) drawLogoFrame(ctx, loop.state, { subpathLengths: measureSubpaths(), ...look, ...size });
    };
    /** Brings the loop up to now. */
    const tick = () => {
        if (!loop) return;
        const now = performance.now();
        loop.advance(now - last);
        last = now;
    };
    const announceFinish = (current: LogoLoop) => {
        if (finishWatch && current.restsInMs !== null) finishWatch.restsAt(performance.now() + current.restsInMs);
    };
    const resize = (nextCssSize: number, nextDpr: number, endScale?: number) => {
        size = { cssSize: nextCssSize, dpr: nextDpr };
        canvas.width = Math.round(nextCssSize * nextDpr);
        canvas.height = Math.round(nextCssSize * nextDpr);
        if (look && endScale !== undefined) look.endScale = endScale;
        redraw();
    };
    resize(cssSize, dpr);

    return {
        start: (theme, endScale) => {
            if (!ctx) return;
            const gen = ++generation;
            const current = (loop = new LogoLoop());
            look = { theme, endScale };
            last = performance.now();
            if (finishWatch) {
                current.finish();
                announceFinish(current);
            }
            const frame = () => {
                if (gen !== generation) return;
                tick();
                redraw();
                if (current.done && finishWatch) {
                    finishWatch.rested();
                    finishWatch = null;
                    return;
                }
                requestAnimationFrame(frame);
            };
            frame();
        },
        finish: (watch) => {
            if (!ctx) {
                watch.restsAt(performance.now());
                watch.rested();
                return;
            }
            finishWatch = watch;
            if (!loop) return;
            tick();
            loop.finish();
            announceFinish(loop);
            // Already at rest (no frame loop left to answer).
            if (loop.done) {
                watch.rested();
                finishWatch = null;
            }
        },
        reset: () => {
            generation++;
            loop = null;
            look = null;
            finishWatch = null;
            if (ctx) clearLogo(ctx);
        },
        resize,
        dispose: () => { generation++; },
    };
}

/**
 * "O" logo for the page-transition overlay, drawn on a canvas.
 *
 * Where supported, the canvas is transferred to a worker so the draw
 * keeps animating while the main thread is blocked by the incoming page.
 * The renderer is created on the first `start`, which on a page refresh
 * is mount, so the logo draws straight away there too.
 */
const Logo = forwardRef<LogoHandle, LogoProps>(({ scale }, ref) => {
    const hostRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const driverRef = useRef<Driver | null>(null);
    const scaleRef = useRef(scale);
    scaleRef.current = scale;
    /** The draw in flight, so a new renderer can pick it up (worker failure,
     *  StrictMode's dev remount). */
    const currentRef = useRef<{ theme: LogoTheme; watch: LogoFinishWatch | null } | null>(null);

    /** A canvas can only be transferred once, so every renderer gets a fresh one. */
    const freshCanvas = () => {
        canvasRef.current?.remove();
        const canvas = document.createElement("canvas");
        canvas.style.width = "100%";
        canvas.style.height = "100%";
        canvas.style.display = "block";
        hostRef.current?.appendChild(canvas);
        canvasRef.current = canvas;
        return canvas;
    };

    /** Restarts the draw in flight, if any, on the current renderer. */
    const replay = () => {
        const driver = driverRef.current;
        const current = currentRef.current;
        if (!driver || !current) return;
        driver.start(current.theme, scaleRef.current);
        if (current.watch) driver.finish(current.watch);
    };

    const fallBackToMainThread = () => {
        driverRef.current?.dispose();
        driverRef.current = createMainThreadDriver(freshCanvas(), LOGO_BOX * scaleRef.current, getDpr());
        replay();
    };

    const ensureDriver = (): Driver | null => {
        if (driverRef.current || !hostRef.current) return driverRef.current;
        const cssSize = LOGO_BOX * scaleRef.current;
        if (canWorkerDraw) {
            try {
                driverRef.current = createWorkerDriver(freshCanvas(), cssSize, getDpr(), fallBackToMainThread);
                return driverRef.current;
            } catch {
                // e.g. a blocked worker URL: draw on the main thread instead.
            }
        }
        driverRef.current = createMainThreadDriver(freshCanvas(), cssSize, getDpr());
        return driverRef.current;
    };

    useImperativeHandle(ref, () => ({
        start: (theme) => {
            currentRef.current = { theme, watch: null };
            ensureDriver()?.start(theme, scaleRef.current);
        },
        finish: (watch) => {
            const driver = ensureDriver();
            if (!driver) {
                watch.restsAt(performance.now());
                watch.rested();
                return;
            }
            if (currentRef.current) currentRef.current.watch = watch;
            driver.finish(watch);
        },
        reset: () => {
            currentRef.current = null;
            driverRef.current?.reset();
        },
    // ensureDriver only touches refs, so the handle never needs rebuilding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), []);

    // A draw started during the first mount (page refresh) survives
    // StrictMode's dev unmount/remount: the remount gets a fresh renderer
    // and replays it. Cleanup also runs on a real unmount.
    useEffect(() => {
        if (currentRef.current && ensureDriver()) replay();
        return () => {
            driverRef.current?.dispose();
            driverRef.current = null;
            canvasRef.current?.remove();
            canvasRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Follows the breakpoint's scale and the screen's pixel density (a move
    // to another monitor, or browser zoom), redrawing at the new size.
    useEffect(() => {
        const resize = () => driverRef.current?.resize(LOGO_BOX * scale, getDpr(), scale);
        resize();
        let query: MediaQueryList | null = null;
        const watchDpr = () => {
            query?.removeEventListener("change", onDprChange);
            query = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
            query.addEventListener("change", onDprChange);
        };
        const onDprChange = () => { resize(); watchDpr(); };
        watchDpr();
        return () => query?.removeEventListener("change", onDprChange);
    }, [scale]);

    return (
        <div
            ref={hostRef}
            style={{ width: LOGO_BOX * scale, height: LOGO_BOX * scale }}
            aria-hidden="true"
        />
    );
});

export default Logo;
